import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';

const slideDown = keyframes`
  from { transform: translateY(-80px); opacity: 0; }
  to   { transform: translateY(0);     opacity: 1; }
`;

const Banner = styled.div`
  position: fixed;
  top: 1rem;
  left: 50%;
  transform: translateX(-50%);
  background: #1e293b;
  color: #f8fafc;
  border-radius: 1rem;
  padding: 1rem 1.25rem;
  width: min(520px, calc(100vw - 2rem));
  z-index: 99999;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.35);
  display: flex;
  align-items: flex-start;
  gap: 0.875rem;
  animation: ${slideDown} 0.35s ease;

  @media (max-width: 480px) {
    padding: 0.875rem 1rem;
    gap: 0.75rem;
    border-radius: 0.75rem;
  }
`;

const Icon = styled.div`
  font-size: 1.75rem;
  flex-shrink: 0;
  line-height: 1;
  margin-top: 0.1rem;
`;

const Content = styled.div`
  flex: 1;
  min-width: 0;
`;

const Title = styled.p`
  margin: 0 0 0.25rem;
  font-weight: 700;
  font-size: 0.95rem;
  color: #f1f5f9;

  @media (max-width: 480px) {
    font-size: 0.875rem;
  }
`;

const Description = styled.p`
  margin: 0 0 0.75rem;
  font-size: 0.82rem;
  color: #94a3b8;
  line-height: 1.5;

  @media (max-width: 480px) {
    font-size: 0.75rem;
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const Btn = styled.button`
  border: none;
  border-radius: 0.5rem;
  padding: 0.45rem 1rem;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  min-height: 36px;

  &.enable {
    background: #3b82f6;
    color: #fff;
    &:hover { background: #2563eb; }
  }

  &.dismiss {
    background: rgba(255, 255, 255, 0.08);
    color: #94a3b8;
    &:hover { background: rgba(255, 255, 255, 0.14); color: #cbd5e1; }
  }
`;

const CloseBtn = styled.button`
  background: none;
  border: none;
  color: #64748b;
  font-size: 1.1rem;
  cursor: pointer;
  padding: 0.25rem;
  line-height: 1;
  flex-shrink: 0;
  transition: color 0.15s;
  &:hover { color: #94a3b8; }
`;

const DISMISSED_KEY = 'push-banner-dismissed-until';

/**
 * Shows a banner asking the patient to enable push notifications.
 * Disappears for 3 days after "Not Now", permanently after enabling.
 */
const NotificationPermissionBanner = ({ onEnable, onDismiss, loading }) => {
  const [hidden, setHidden] = useState(() => {
    try {
      const until = localStorage.getItem(DISMISSED_KEY);
      return until ? Date.now() < parseInt(until, 10) : false;
    } catch {
      return false;
    }
  });

  if (hidden) return null;

  const handleDismiss = () => {
    try {
      // Remind again in 3 days
      localStorage.setItem(DISMISSED_KEY, String(Date.now() + 3 * 24 * 60 * 60 * 1000));
    } catch {}
    setHidden(true);
    onDismiss?.();
  };

  const handleEnable = () => {
    try {
      localStorage.removeItem(DISMISSED_KEY);
    } catch {}
    onEnable?.();
    setHidden(true);
  };

  return (
    <Banner role="alert" aria-live="polite">
      <Icon>🔔</Icon>
      <Content>
        <Title>Stay on top of your medication</Title>
        <Description>
          Enable notifications to get instant alerts when your doctor sends a new
          prescription — even when the app is closed.
        </Description>
        <Actions>
          <Btn className="enable" onClick={handleEnable} disabled={loading}>
            {loading ? 'Enabling…' : 'Enable Notifications'}
          </Btn>
          <Btn className="dismiss" onClick={handleDismiss}>
            Not Now
          </Btn>
        </Actions>
      </Content>
      <CloseBtn onClick={handleDismiss} aria-label="Close notification banner">×</CloseBtn>
    </Banner>
  );
};

export default NotificationPermissionBanner;
